import React, { useState, useEffect, useCallback } from "react";
import { X, Smartphone, Download, Share } from "lucide-react";
import {
  isPWAInstalled,
  canInstallPWA,
  requestPWAInstall,
  showiOSInstallInstructions,
  isMobileDevice,
  setInstallPrompt,
  clearInstallPrompt,
  logPWAInstall,
} from "../../utils/pwa-utils";

const InstallPrompt = () => {
  const [showPrompt, setShowPrompt] = useState(false);
  const [showIOSInstructions, setShowIOSInstructions] = useState(false);
  const [installPrompt, setInstallPromptState] = useState(null);
  const [isInstalling, setIsInstalling] = useState(false);

  const iosInfo = showiOSInstallInstructions();
  const isMobile = isMobileDevice();

  // Handle beforeinstallprompt event
  useEffect(() => {
    const handleBeforeInstallPrompt = (e) => {
      console.log("📱 Before install prompt event triggered");

      // Prevent Chrome 67 and earlier from automatically showing the prompt
      e.preventDefault();

      // Store the event for later use
      setInstallPrompt(e);
      setInstallPromptState(e);

      // Only show prompt if PWA is not already installed
      if (!isPWAInstalled() && canInstallPWA()) {
        setShowPrompt(true);
      }
    };

    const handleAppInstalled = () => {
      console.log("📱 App was installed");
      setShowPrompt(false);
      setShowIOSInstructions(false);
      clearInstallPrompt();
      logPWAInstall();
    };

    window.addEventListener("beforeinstallprompt", handleBeforeInstallPrompt);
    window.addEventListener("appinstalled", handleAppInstalled);

    // Check initial state
    if (isPWAInstalled()) {
      setShowPrompt(false);
    } else if (iosInfo.isIOS && iosInfo.isSafari) {
      // Show iOS instructions after a delay
      const timer = setTimeout(() => {
        if (!isPWAInstalled()) {
          setShowIOSInstructions(true);
        }
      }, 5000); // Show after 5 seconds

      return () => clearTimeout(timer);
    }

    return () => {
      window.removeEventListener(
        "beforeinstallprompt",
        handleBeforeInstallPrompt
      );
      window.removeEventListener("appinstalled", handleAppInstalled);
    };
  }, [iosInfo.isIOS, iosInfo.isSafari]);

  const handleInstall = useCallback(async () => {
    if (!installPrompt) {
      console.log("📱 No install prompt available");
      return;
    }

    setIsInstalling(true);

    try {
      const success = await requestPWAInstall();

      if (success) {
        setShowPrompt(false);
        console.log("📱 Installation successful");
      } else {
        console.log("📱 Installation cancelled or failed");
      }
    } catch (error) {
      console.error("📱 Installation error:", error);
    } finally {
      setIsInstalling(false);
    }
  }, [installPrompt]);

  const handleDismiss = useCallback(() => {
    setShowPrompt(false);

    // Don't show again for this session
    sessionStorage.setItem("pwa-prompt-dismissed", "true");
  }, []);

  const handleDismissIOS = useCallback(() => {
    setShowIOSInstructions(false);

    // Don't show again for this session
    sessionStorage.setItem("ios-install-dismissed", "true");
  }, []);

  // Check if prompt was already dismissed this session
  useEffect(() => {
    const wasDismissed = sessionStorage.getItem("pwa-prompt-dismissed");
    const iosWasDismissed = sessionStorage.getItem("ios-install-dismissed");

    if (wasDismissed) {
      setShowPrompt(false);
    }

    if (iosWasDismissed) {
      setShowIOSInstructions(false);
    }
  }, []);

  // Standard PWA Install Prompt
  if (showPrompt && !iosInfo.isIOS) {
    return (
      <div className="fixed bottom-4 left-4 right-4 md:left-auto md:right-4 md:max-w-sm bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 animate-slide-up">
        <div className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <Smartphone className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                  Install CRM Chat
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Get the full app experience
                </p>
              </div>
            </div>
            <button
              onClick={handleDismiss}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Dismiss install prompt"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-2 mb-4">
            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
              <span className="w-1 h-1 bg-green-500 rounded-full"></span>
              <span>Faster loading & offline access</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
              <span className="w-1 h-1 bg-green-500 rounded-full"></span>
              <span>Push notifications for new messages</span>
            </div>
            <div className="flex items-center gap-2 text-xs text-gray-600 dark:text-gray-400">
              <span className="w-1 h-1 bg-green-500 rounded-full"></span>
              <span>Home screen access</span>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={handleDismiss}
              className="flex-1 px-3 py-2 text-sm text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Not now
            </button>
            <button
              onClick={handleInstall}
              disabled={isInstalling}
              className="flex-1 px-3 py-2 text-sm text-white bg-blue-500 rounded-md hover:bg-blue-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isInstalling ? (
                <>
                  <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                  Installing...
                </>
              ) : (
                <>
                  <Download className="w-3 h-3" />
                  Install
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    );
  }

  // iOS Safari Install Instructions
  if (showIOSInstructions && iosInfo.isIOS && iosInfo.isSafari) {
    return (
      <div className="fixed bottom-4 left-4 right-4 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 z-50 animate-slide-up">
        <div className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                <Share className="w-5 h-5 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 dark:text-white text-sm">
                  Add to Home Screen
                </h3>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  Install CRM Chat for quick access
                </p>
              </div>
            </div>
            <button
              onClick={handleDismissIOS}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
              aria-label="Dismiss install instructions"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          <div className="space-y-3 mb-4">
            {iosInfo.steps.map((step, index) => (
              <div key={index} className="flex items-start gap-3">
                <div className="w-5 h-5 bg-blue-500 text-white rounded-full flex items-center justify-center text-xs font-medium flex-shrink-0 mt-0.5">
                  {index + 1}
                </div>
                <p className="text-sm text-gray-700 dark:text-gray-300">
                  {step}
                </p>
              </div>
            ))}
          </div>

          <button
            onClick={handleDismissIOS}
            className="w-full px-3 py-2 text-sm text-gray-600 dark:text-gray-400 bg-gray-100 dark:bg-gray-700 rounded-md hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Got it
          </button>
        </div>
      </div>
    );
  }

  return null;
};

// Add CSS for slide-up animation
const style = document.createElement("style");
style.textContent = `
  @keyframes slide-up {
    from {
      transform: translateY(100%);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
  
  .animate-slide-up {
    animation: slide-up 0.3s ease-out;
  }
`;
document.head.appendChild(style);

export default InstallPrompt;
